/**
 * Pikestee product cards: colour swatches (snippets/card-swatches.liquid).
 * Hover or click on a swatch shows that colour's photo on the card and points the card links to that variant.
 * One listener for the whole page (it used to be an inline script repeated on every swatch).
 *
 * <pk-lazy-section> (speed): sections/featured-collection.liquid on product pages and the collection grid of
 * sections/collection-carousel.liquid only carry their title and this empty box in the page; the rest is fetched
 * with the Section Rendering API a little before the box scrolls into view, then put in its place (same HTML as a
 * full render, reveal animations included).
 * data-src: section URL; data-target: id of the element whose content replaces the box; data-skip: what of that
 * content is already on the page (the title), default .collection__title.
 */
(() => {
  if (window.__pkCardSwatches) return;
  window.__pkCardSwatches = true;

  const apply = (swatch) => {
    const card = swatch.closest('.card-wrapper');
    if (!card) return;
    const image = card.querySelector('.card__media img');
    if (image && swatch.dataset.src) {
      image.srcset = swatch.dataset.srcset;
      image.src = swatch.dataset.src;
    }
    card.classList.add('is-swatched');
    card.querySelectorAll('.card-swatch').forEach((button) => button.classList.toggle('is-active', button === swatch));
    if (swatch.dataset.url) {
      card.querySelectorAll('a.full-unstyled-link').forEach((link) => {
        link.href = swatch.dataset.url;
      });
    }
  };

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.('.card-swatch');
    if (swatch) apply(swatch);
  });

  // Same as mouseenter on each swatch (mouseover bubbles, mouseenter does not)
  document.addEventListener('mouseover', (event) => {
    const swatch = event.target.closest?.('.card-swatch');
    if (!swatch || swatch.contains(event.relatedTarget)) return;
    apply(swatch);
  });

  class PkLazySection extends HTMLElement {
    connectedCallback() {
      if (this.observer || this.started) return;
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        },
        { rootMargin: '1500px 0px' },
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
    }

    async load() {
      if (this.started) return;
      this.started = true;
      this.observer?.disconnect();
      try {
        const response = await fetch(this.dataset.src);
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.getElementById(this.dataset.target);
        if (!fresh || !this.parentElement || fresh.querySelector('pk-lazy-section')) throw new Error('no content');
        // The title is already on the page: only the rest is added
        fresh.querySelectorAll(this.dataset.skip || '.collection__title').forEach((node) => node.remove());
        const nodes = [...fresh.childNodes].map((node) => document.importNode(node, true));
        this.replaceWith(...nodes);
        if (typeof initializeScrollAnimationTrigger === 'function') {
          const animated = nodes.filter((node) => node.nodeType === 1);
          const wrapper = { getElementsByClassName: (name) => animated.flatMap((node) => [...(node.classList.contains(name) ? [node] : []), ...node.getElementsByClassName(name)]) };
          initializeScrollAnimationTrigger(wrapper);
        }
      } catch (error) {
        this.started = false;
      }
    }
  }

  if (!customElements.get('pk-lazy-section')) customElements.define('pk-lazy-section', PkLazySection);
})();
